<template>
  <div class="w-full flex flex-col justify-center items-center gap-y-3">
    <div class="flex items-center justify-between border-b border-brand-gray-350 w-full mb-4">
      <div class="pb-2 w-full">
        <h2 class="text-xl text-gray-700 font-normal mx-3">
          {{ $t('establishmentCard') }}
        </h2>
      </div>
    </div>
    <div class="print:hidden max-w-[32rem] w-full flex">
      <button
        class="rounded-md bg-brand-green/80 pe-2 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        @click="onPrintClick"
      >
        <div class="p-2 bg-brand-green rounded-s-md">
          <faPrint class="w-4 fill-white"></faPrint>
        </div>
        <span class="w-full text-center">
          {{ $t('printEstablishmentCard') }}
        </span>
        <div class="p-0.5"></div>
      </button>
    </div>
    <div
      v-if="!isLoading"
      class="p-2 max-w-[32rem] w-full flex flex-col gap-y-3 rounded-lg border-2 border-brand-black-secondary shadow-lg ltr:font-sans text-xs text-brand-black-secondary"
    >
      <div class="w-full flex justify-between">
        <p class="text-center flex-1 flex flex-col justify-center items-center gap-y-0.5">
          <span>دولة الإمارات العربية المتحدة</span>
          <span class="max-w-[18ch]">{{ estInfo.arabicEstablishmentDepartmentName }}</span>
        </p>
        <img :src="logoGif" class="size-12" />
        <p class="text-center flex-1 flex flex-col justify-center items-center gap-y-0.5">
          <span>United Arab Emirates</span>
          <span class="max-w-[22ch]">{{ estInfo.englishEstablishmentDepartmentName }}</span>
        </p>
      </div>
      <div class="w-full flex justify-center">
        <span class="px-2.5 py-0.5 bg-brand-black-body text-white">
          {{ $t('establishmentCard') }}
        </span>
      </div>
      <div dir="ltr" class="flex flex-col gap-y-3 px-4">
        <div class="flex flex-col gap-y-3">
          <div class="w-full flex items-center gap-x-6">
            <span class="w-1/12">Number:</span>
            <small class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm">
              {{ estInfo.establishmentCardNumber }}/{{ estInfo.estabDeptCode }}
            </small>
          </div>
          <div dir="rtl" class="w-full flex items-center gap-x-6">
            <span class="w-1/12">الاسم:</span>
            <small class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm">
              {{ estInfo.establishmentCardNumber }}/{{ estInfo.estabDeptCode }}
            </small>
          </div>
        </div>
        <div class="flex flex-col gap-y-3">
          <div class="w-full flex items-center gap-x-6">
            <span class="w-1/12">Name:</span>
            <small
              class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm tracking-tighter"
            >
              {{ estInfo.englishEstablishmentName }}
            </small>
          </div>
        </div>
        <div class="flex flex-col gap-y-3">
          <div class="w-full flex items-center gap-x-6">
            <span class="w-1/12">Activity:</span>
            <small
              class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm tracking-tighter"
            >
              {{ estInfo.englishEconomicActivity || estInfo.arabicEconomicActivity }}
            </small>
          </div>
        </div>
        <div class="flex flex-col gap-y-3">
          <div class="w-full flex items-center gap-x-6">
            <span class="w-2/12">Category:</span>
            <div class="flex gap-x-4">
              <div
                v-if="estInfo.isVisitVisaFlag"
                class="py-0.5 px-2 border border-brand-black-body rounded-sm"
              >
                {{ $t('visit') }}
              </div>
              <div
                v-if="estInfo.isTouristVisaFlag"
                class="py-0.5 px-2 border border-brand-black-body rounded-sm"
              >
                {{ $t('tourism') }}
              </div>
              <div
                v-if="estInfo.isSeviceVisaFlag"
                class="py-0.5 px-2 border border-brand-black-body rounded-sm"
              >
                {{ $t('work') }}
              </div>
            </div>
          </div>
        </div>
        <div class="w-full flex gap-x-4">
          <div class="w-4/6 flex flex-col gap-y-3">
            <div class="flex-1 flex items-start gap-x-2">
              <span class="w-1/3">{{ $t('releasedate') }}:</span>
              <small
                class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm tracking-tighter"
              >
                {{ estInfo.establishmentCardIssueDatePretty }}
              </small>
            </div>
            <div class="flex-1 flex items-start gap-x-2">
              <span class="w-1/3">{{ $t('enddate') }}:</span>
              <small
                class="ps-3 pb-0.5 flex-1 border-b border-dashed border-brand-gray-600 text-sm tracking-tighter"
              >
                {{ estInfo.establishmentCardExpiryDatePretty }}
              </small>
            </div>
          </div>
          <div class="w-2/6 flex flex-col justify-center items-center gap-y-3">
            <p class="text-center">{{ $t('executiveDirectorofResidenceandForeignAffairs') }}</p>
            <p>{{ estInfo.directorSignature }}</p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isLoading"
      class="p-0.5 max-w-[32rem] w-full flex flex-col gap-y-3 rounded-lg border-2 border-brand-black-secondary shadow-lg ltr:font-sans text-xs text-brand-black-secondary"
    >
      <DataTable
        :default-headers="['signature', 'personRole', 'theName', 'number']"
        :rows="rows"
      ></DataTable>
    </div>
  </div>
</template>

<script setup>
import logoGif from '@/assets/gifs/logo.gif'
import faPrint from '@/assets/icons/faPrint.svg?component'
import DataTable from '@/components/DataTable/index.vue'
import axiosInstance from '@/services'
import { DateTime } from 'luxon'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const estInfo = ref()
const authorizedPeopleList = ref()
const isLoading = ref(true)

const rows = computed(() => {
  if ((authorizedPeopleList.value ?? []).length === 0) return

  return authorizedPeopleList.value.map((l) => {
    return {
      number: l.personUnifiedNumber,
      theName: l.englishPersonName || l.arabicPersonName,
      personRole: l.personType.text,
      signature: l.authorizedPersonSignature
    }
  })
})

const formatDate = (date) => DateTime.fromISO(date).toFormat('dd/MM/yyyy')

onMounted(async () => {
  const path = `/api/establishment/getEstablishmentCardInformation/${route.query?.childEstablishmentId || undefined}`
  const response = await axiosInstance.get(path)

  const { establishmentCardResultAuthorizedPersonDtoList } = response
  estInfo.value = {
    ...response,
    establishmentCardIssueDatePretty: formatDate(response.establishmentCardIssueDate),
    establishmentCardExpiryDatePretty: formatDate(response.establishmentCardExpiryDate)
  }
  authorizedPeopleList.value = establishmentCardResultAuthorizedPersonDtoList
  isLoading.value = false
})

const onPrintClick = () => {
  window.print()
}
</script>

<style lang="scss" scoped></style>
